/**
 * Cart Subscribe & Save (BÉTON)
 * - Works with Recharge if available (RechargeCartProxy)
 * - Fallback to Shopify native selling_plan via /cart/change.js
 * - Robust for AJAX cart drawer refresh (DOM replaced)
 * - Visual checkbox state is forced via .is-checked on label (no fragile sibling CSS)
 */
class CartSubscribeSave extends HTMLElement {
  constructor() {
    super();
    this._initialized = false;
    this._docClickHandler = null;
    this._onCheckboxChange = null;
    this._onToggleClick = null;
    this._onOptionClicks = [];
  }

  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    // --- Data ---
    this.productId = this.dataset.productId || "";
    this.variantId = this.dataset.variantId || "";
    this.itemKey = this.dataset.itemKey || "";
    this.originalPrice = parseInt(this.dataset.price || "0", 10) || 0;

    this.currentSellingPlan = this.dataset.currentSellingPlan || "";
    this.currentSellingPlanName = this.dataset.currentSellingPlanName || "";
    this.isSubscription = this.dataset.isSubscription === "true";
    this.currentFrequency = "30";
    this.isProcessing = false;

    // --- Translations ---
    this.translations = {
      subscribe: this.dataset.textSubscribe || "S’abonner",
      subscribed: this.dataset.textSubscribed || "Abonné",
      thirtyDays: this.dataset.text30Days || "30 jours",
      sixtyDays: this.dataset.text60Days || "60 jours",
      thirtyDaysDiscount: this.dataset.text30DaysDiscount || "30 jours (-15%)",
      sixtyDaysDiscount: this.dataset.text60DaysDiscount || "60 jours (-10%)",
      switching: this.dataset.textSwitching || "Chargement…",
      successSubscribed: this.dataset.successSubscribed || "Abonnement activé!",
      successRemoved: this.dataset.successRemoved || "Abonnement retiré!",
      successFrequencyUpdated: this.dataset.successFrequencyUpdated || "Fréquence mise à jour!",
    };

    // Selling plans map: { "30": "123", "60": "456" }
    this.sellingPlans = this.buildSellingPlansMap();

    // If already subscribed, detect frequency from selling plan id
    if (this.isSubscription && this.currentSellingPlan) {
      this.currentFrequency =
        this.detectFrequencyFromSellingPlanId(this.currentSellingPlan) ||
        this.detectFrequencyFromPlanName(this.currentSellingPlanName) ||
        this.currentFrequency;
    } else if (!this.sellingPlans[this.currentFrequency]) {
      this.currentFrequency = Object.keys(this.sellingPlans)[0] || "30";
    }

    // --- Cache DOM ---
    this.$label = this.querySelector(".cart-subscribe-save__checkbox-label");
    this.$checkbox = this.querySelector("[data-subscription-checkbox]");
    this.$freqToggle = this.querySelector("[data-frequency-toggle]");
    this.$dropdown = this.querySelector(".cart-subscribe-save__frequency-dropdown");
    this.$freqDays = this.querySelector(".cart-subscribe-save__frequency-days");
    this.$discount = this.querySelector(".cart-subscribe-save__discount");
    this.$container = this.querySelector(".cart-subscribe-save__container");
    this.$loading = this.querySelector(".cart-subscribe-save__loading");
    this.$text = this.querySelector(".cart-subscribe-save__text");
    this.$labelText = this.querySelector("[data-subscription-label]");

    // First render
    this.render();
    this.bindEvents();
  }

  disconnectedCallback() {
    // Cleanup document click handler
    if (this._docClickHandler) {
      document.removeEventListener("click", this._docClickHandler);
      this._docClickHandler = null;
    }
  }

  // ----------------------------
  // Selling plans parsing
  // ----------------------------
  buildSellingPlansMap() {
    let plans = [];

    // Prefer JSON (more reliable)
    try {
      const rawJson = this.dataset.sellingPlansJson;
      if (rawJson) plans = JSON.parse(rawJson);
    } catch (e) {
      // ignore
    }

    // Fallback: "id:name,id:name"
    if (!Array.isArray(plans) || !plans.length) {
      const raw = this.dataset.sellingPlans;
      if (raw) {
        raw.split(",").forEach((pair) => {
          const [id, name] = pair.split(":");
          if (id && name) plans.push({ id, name });
        });
      }
    }

    const map = {};
    plans.forEach((p, index) => {
      const freq = this.detectFrequencyFromPlanName(p?.name);
      const fallbackFreq = index === 0 ? "30" : index === 1 ? "60" : null;
      const resolvedFreq = freq || fallbackFreq;

      if (p?.id != null && resolvedFreq && !map[resolvedFreq]) {
        map[resolvedFreq] = String(p.id);
      }
    });

    if (!map["30"] && plans[0]?.id != null) map["30"] = String(plans[0].id);
    if (!map["60"] && plans[1]?.id != null) map["60"] = String(plans[1].id);

    return map;
  }

  detectFrequencyFromPlanName(name) {
    const n = String(name || "").toLowerCase();
    if (
      n.includes("60") ||
      n.includes("8 semaine") ||
      n.includes("8 week") ||
      n.includes("2 mois") ||
      n.includes("2 month") ||
      n.includes("two month")
    ) return "60";
    if (
      n.includes("30") ||
      n.includes("4 semaine") ||
      n.includes("4 week") ||
      n.includes("1 mois") ||
      n.includes("1 month") ||
      n.includes("monthly") ||
      n.includes("mensuel")
    ) return "30";
    return null;
  }

  detectFrequencyFromSellingPlanId(id) {
    const s = String(id || "");
    for (const [freq, planId] of Object.entries(this.sellingPlans)) {
      if (String(planId) === s) return freq;
    }
    return null;
  }

  // ----------------------------
  // Events
  // ----------------------------
  bindEvents() {
    // Checkbox
    if (this.$checkbox) {
      this._onCheckboxChange = async (e) => {
        if (this.isProcessing) return;
        await this.toggleSubscription(e.target.checked);
      };
      this.$checkbox.addEventListener("change", this._onCheckboxChange);
    }

    // Frequency dropdown
    if (this.$freqToggle && this.$dropdown) {
      this._onToggleClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.toggleDropdown();
      };
      this.$freqToggle.addEventListener("click", this._onToggleClick);

      // Options
      this.$dropdown.querySelectorAll(".cart-subscribe-save__frequency-option").forEach((btn) => {
        const handler = async (e) => {
          e.preventDefault();
          e.stopPropagation();
          if (this.isProcessing) return;

          const newFreq = btn.dataset.frequency;
          this.hideDropdown();
          await this.changeFrequency(newFreq);
        };
        btn.addEventListener("click", handler);
        this._onOptionClicks.push({ btn, handler });
      });

      // Close when clicking outside
      this._docClickHandler = (e) => {
        if (!this.contains(e.target)) this.hideDropdown();
      };
      document.addEventListener("click", this._docClickHandler);
    }
  }

  // ----------------------------
  // Render / UI
  // ----------------------------
  render() {
    // 1) Checkbox state
    if (this.$checkbox) this.$checkbox.checked = !!this.isSubscription;

    // 2) Force visual state on label (BÉTON)
    if (this.$label) this.$label.classList.toggle("is-checked", !!this.isSubscription);

    // 3) Root class for styling
    this.classList.toggle("is-subscription", !!this.isSubscription);

    // 4) Text state (replace first text node only)
    const labelText = this.isSubscription ? this.translations.subscribed : this.translations.subscribe;
    if (this.$labelText) {
      this.$labelText.textContent = labelText;
    } else if (this.$text) {
      const firstNode = this.$text.childNodes[0];
      if (firstNode && firstNode.nodeType === Node.TEXT_NODE) {
        firstNode.textContent = labelText + " ";
      }
    }

    // 5) Frequency UI
    this.updateFrequencyUI();
  }

  updateFrequencyUI() {
    if (this.$freqDays) {
      this.$freqDays.textContent =
        this.currentFrequency === "60" ? this.translations.sixtyDays : this.translations.thirtyDays;
    }
    if (this.$discount) {
      this.$discount.textContent = this.currentFrequency === "60" ? "-10%" : "-15%";
    }
    this.updateDropdownActiveState();
  }

  toggleDropdown() {
    if (!this.$dropdown) return;
    if (this.isProcessing) return;

    const visible = !this.$dropdown.hidden;
    this.$dropdown.hidden = visible;
    if (this.$freqToggle) this.$freqToggle.setAttribute("aria-expanded", String(!visible));
    this.updateDropdownActiveState();
  }

  hideDropdown() {
    if (this.$dropdown) this.$dropdown.hidden = true;
    if (this.$freqToggle) this.$freqToggle.setAttribute("aria-expanded", "false");
  }

  updateDropdownActiveState() {
    if (!this.$dropdown) return;
    this.$dropdown.querySelectorAll(".cart-subscribe-save__frequency-option").forEach((btn) => {
      btn.classList.toggle("active", btn.dataset.frequency === this.currentFrequency);
    });
  }

  setLoading(loading) {
    this.isProcessing = loading;
    this.classList.toggle("is-loading", loading);
    this.setAttribute("aria-busy", String(loading));

    if (this.$checkbox) this.$checkbox.disabled = loading;
    if (this.$freqToggle) this.$freqToggle.disabled = loading;
    if (this.$dropdown) {
      this.$dropdown.querySelectorAll("button").forEach((button) => {
        button.disabled = loading;
      });
    }

    if (this.$container && this.$loading) {
      this.$loading.style.display = loading ? "flex" : "none";
    }

    if (loading) this.hideDropdown();
  }

  // ----------------------------
  // Logic
  // ----------------------------
  async toggleSubscription(checked) {
    if (checked && !this.isSubscription) return this.convertToSubscription();
    if (!checked && this.isSubscription) return this.removeSubscription();
  }

  async changeFrequency(newFrequency) {
    if (!newFrequency) return;

    // On bloque si pas abonné (évite confusion)
    if (!this.isSubscription) return;

    if (newFrequency === this.currentFrequency) return;

    const previous = this.currentFrequency;
    this.currentFrequency = newFrequency;
    this.updateFrequencyUI();

    try {
      await this.updateSubscriptionFrequency(newFrequency);
      this.toast(this.translations.successFrequencyUpdated);
    } catch (e) {
      console.error(e);
      this.currentFrequency = previous;
      this.updateFrequencyUI();
      this.toast("Erreur: impossible de changer la fréquence", true);
    }
  }

  getSellingPlanIdForCurrentFrequency() {
    const planId = this.sellingPlans[this.currentFrequency];
    return planId ? String(planId) : null;
  }

  async getCurrentQuantity() {
    // Try DOM first
    const row = this.findCartLineElement();
    const input = row?.querySelector(`input[name="updates[]"]`);
    if (input?.value) return parseInt(input.value, 10) || 1;

    const rowQty = parseInt(row?.dataset?.qty || "", 10);
    if (Number.isFinite(rowQty) && rowQty > 0) return rowQty;

    // Fallback cart.js
    try {
      const res = await fetch("/cart.js");
      if (res.ok) {
        const cart = await res.json();
        const it = cart.items.find((x) => x.key === this.itemKey);
        if (it) return it.quantity;
      }
    } catch (e) {
      // ignore
    }
    return 1;
  }

  findCartLineElement() {
    const rows = document.querySelectorAll("[data-js-cart-item]");

    return Array.from(rows).find((row) => {
      return [
        row.dataset.id,
        row.dataset.cartItemKey,
        row.dataset.lineItemKey,
        row.getAttribute("data-key")
      ].some((value) => value && value === this.itemKey);
    }) || null;
  }

  // ----------------------------
  // Actions (Recharge -> Shopify fallback)
  // ----------------------------
  async convertToSubscription() {
    const sellingPlanId = this.getSellingPlanIdForCurrentFrequency();
    if (!sellingPlanId) {
      this.toast("Aucun plan d’abonnement trouvé pour cette fréquence.", true);
      if (this.$checkbox) this.$checkbox.checked = false;
      if (this.$label) this.$label.classList.remove("is-checked");
      return;
    }

    try {
      this.setLoading(true);
      const qty = await this.getCurrentQuantity();

      // Optimistic UI (instant)
      this.isSubscription = true;
      this.render();

      // 1) Recharge
      if (window.RechargeCartProxy?.convertToSubscription) {
        await window.RechargeCartProxy.convertToSubscription({
          variantId: this.variantId,
          quantity: qty,
          frequency: this.currentFrequency,
          itemKey: this.itemKey,
          sellingPlanId
        });
      } else {
        // 2) Shopify fallback
        await this.shopifyChangeLine({ sellingPlanId, quantity: qty });
      }

      this.toast(this.translations.successSubscribed);
      await this.refreshCartUI();

    } catch (e) {
      console.error("Subscription creation failed:", e);

      // Revert UI
      this.isSubscription = false;
      this.render();
      if (this.$checkbox) this.$checkbox.checked = false;

      this.toast("Erreur: abonnement non activé.", true);
    } finally {
      this.setLoading(false);
    }
  }

  async removeSubscription() {
    try {
      this.setLoading(true);
      const qty = await this.getCurrentQuantity();

      // Optimistic UI (instant)
      this.isSubscription = false;
      this.render();

      if (window.RechargeCartProxy?.removeSubscription) {
        await window.RechargeCartProxy.removeSubscription({
          itemKey: this.itemKey,
          variantId: this.variantId,
          quantity: qty
        });
      } else {
        await this.shopifyChangeLine({ sellingPlanId: null, quantity: qty });
      }

      this.toast(this.translations.successRemoved);
      await this.refreshCartUI();

    } catch (e) {
      console.error("Subscription removal failed:", e);

      // Revert UI
      this.isSubscription = true;
      this.render();
      if (this.$checkbox) this.$checkbox.checked = true;

      this.toast("Erreur: impossible de retirer l’abonnement.", true);
    } finally {
      this.setLoading(false);
    }
  }

  async updateSubscriptionFrequency(newFrequency) {
    const planId = this.sellingPlans[newFrequency];
    if (!planId) throw new Error("No selling plan for frequency " + newFrequency);

    this.setLoading(true);
    try {
      const qty = await this.getCurrentQuantity();

      if (window.RechargeCartProxy?.updateSubscriptionFrequency) {
        await window.RechargeCartProxy.updateSubscriptionFrequency({
          frequency: newFrequency,
          itemKey: this.itemKey,
          variantId: this.variantId,
          quantity: qty,
          newSellingPlanId: String(planId)
        });
      } else {
        await this.shopifyChangeLine({ sellingPlanId: String(planId), quantity: qty });
      }

      this.currentFrequency = newFrequency;
      this.updateFrequencyUI();
      await this.refreshCartUI();
    } finally {
      this.setLoading(false);
    }
  }

  async shopifyChangeLine({ sellingPlanId, quantity }) {
    const payload = {
      id: this.itemKey,
      quantity: Number(quantity) || 1
    };

    if (sellingPlanId !== undefined) {
      payload.selling_plan = sellingPlanId === null ? null : String(sellingPlanId);
    }

    const res = await fetch("/cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", "Accept": "application/json" },
      body: JSON.stringify(payload)
    });

    if (!res.ok) {
      let message = res.statusText;
      try {
        const data = await res.json();
        message = data.description || data.message || message;
      } catch (e) {
        // ignore JSON parse errors and keep the status text
      }
      throw new Error(`cart/change.js failed: ${message}`);
    }

    return res.json();
  }

  // ----------------------------
  // Cart refresh + Resync (BÉTON)
  // ----------------------------
  async refreshCartUI() {
    const themeRefresh =
      window.ShopSanteRefreshCart ||
      (typeof refreshHelperCartNow === "function" ? refreshHelperCartNow : null);

    if (typeof themeRefresh === "function") {
      await themeRefresh();
      this.resyncFromFreshDom();
      return;
    }

    // Best: use theme refresh if exists (drawer usually re-renders)
    if (typeof window.refreshCart === "function") {
      window.refreshCart();
      setTimeout(() => this.resyncFromFreshDom(), 250);
      return;
    }

    // Dispatch events (other scripts)
    document.dispatchEvent(new CustomEvent("cart:updated"));
    document.dispatchEvent(new CustomEvent("cart:refresh"));

    // Fallback: refresh helper-cart section
    try {
      const res = await fetch(`${window.location.href}?section_id=helper-cart`);
      if (!res.ok) {
        setTimeout(() => this.resyncFromFreshDom(), 350);
        return;
      }

      const text = await res.text();
      const doc = new DOMParser().parseFromString(text, "text/html");

      const newForm = doc.getElementById("AjaxCartForm")?.innerHTML;
      const newSubtotal = doc.getElementById("AjaxCartSubtotal")?.innerHTML;
      const current = document.getElementById("AjaxCartForm");
      const subtotal = document.getElementById("AjaxCartSubtotal");

      if (current && newForm) {
        current.innerHTML = newForm;
        if (typeof current.ajaxifyCartItems === "function") {
          current.ajaxifyCartItems();
        }
      }

      if (subtotal && newSubtotal) {
        subtotal.innerHTML = newSubtotal;
      }

      document.querySelectorAll("[data-header-cart-count]").forEach((elm) => {
        const count = current?.querySelector("[data-cart-count]");
        if (count) elm.textContent = count.textContent;
      });

      document.querySelectorAll("[data-header-cart-total]").forEach((elm) => {
        const total = current?.querySelector("[data-cart-total]");
        if (total) elm.textContent = total.textContent;
      });

      setTimeout(() => this.resyncFromFreshDom(), 350);

    } catch (e) {
      console.error("refreshCartUI failed:", e);
      setTimeout(() => this.resyncFromFreshDom(), 400);
    }
  }

  /**
   * Re-sync UI from the newly rendered widget in the DOM (after cart refresh)
   * Source of truth: data-is-subscription OR data-current-selling-plan
   */
  resyncFromFreshDom() {
    const newEl = document.querySelector(`cart-subscribe-save[data-item-key="${this.itemKey}"]`);
    if (!newEl) return;

    const cb = newEl.querySelector('[data-subscription-checkbox]');
    const label = newEl.querySelector(".cart-subscribe-save__checkbox-label");

    const isSub =
      newEl.dataset.isSubscription === "true" ||
      (!!newEl.dataset.currentSellingPlan && newEl.dataset.currentSellingPlan !== "");

    // Force visual checkbox
    if (cb) cb.checked = !!isSub;
    if (label) label.classList.toggle("is-checked", !!isSub);

    // Root class
    newEl.classList.toggle("is-subscription", !!isSub);

    // Text
    const textEl = newEl.querySelector(".cart-subscribe-save__text");
    const labelTextEl = newEl.querySelector("[data-subscription-label]");
    const nextText = isSub ? (newEl.dataset.textSubscribed || "Abonné") : (newEl.dataset.textSubscribe || "S’abonner");

    if (labelTextEl) {
      labelTextEl.textContent = nextText;
    } else if (textEl) {
      const firstNode = textEl.childNodes[0];
      if (firstNode && firstNode.nodeType === Node.TEXT_NODE) {
        firstNode.textContent = nextText + " ";
      }
    }
  }

  // ----------------------------
  // Toast
  // ----------------------------
  toast(message, isError = false) {
    const el = document.createElement("div");
    el.textContent = message;
    el.style.cssText = `
      position: fixed;
      right: 16px;
      bottom: 16px;
      z-index: 999999;
      background: ${isError ? "#ffe8e8" : "#e8fff0"};
      color: #111;
      border: 1px solid rgba(0,0,0,.08);
      box-shadow: 0 10px 30px rgba(0,0,0,.08);
      padding: 10px 12px;
      border-radius: 10px;
      font-size: 13px;
      max-width: 320px;
    `;
    document.body.appendChild(el);
    setTimeout(() => el.remove(), 2200);
  }
}

// Register element once
if (!customElements.get("cart-subscribe-save")) {
  customElements.define("cart-subscribe-save", CartSubscribeSave);
}
